import { Link } from "react-router-dom";
import { BRAND } from "@/lib/brandingConfig";

/**
 * HarborWellFormHeader — shared header for standalone / public /
 * authentication / invitation form pages.
 *
 * Renders the official HarborWell logo (from brandingConfig) with
 * consistent sizing, accessible alternative text, and optional
 * eyebrow / title / supporting-text slots. Responsive across mobile,
 * tablet, and desktop.
 *
 * Do not use inside modals, dialogs, inline edit widgets, or admin
 * CRUD forms that already render inside the authenticated
 * HarborWell Connect application shell — that shell already carries
 * the primary logo (see components/Layout.jsx). Reserve this header
 * for standalone pages where the visitor may reasonably question
 * whether the form is operated by HarborWell.
 *
 * Props:
 *   eyebrow?         : node    - optional small caps eyebrow above the title
 *   title?           : node    - optional H-style title text or element
 *   supportingText?  : node    - optional descriptive line under the title
 *   variant?         : 'light' | 'dark'  (default 'light')
 *   size?            : 'sm' | 'md' | 'lg'  (logo height; default 'md')
 *   align?           : 'left' | 'center'  (default 'left')
 *   homeHref?        : string  (default '/')
 *   linkLogoHome?    : boolean (default true)
 *   rightSlot?       : node    - optional right-aligned actions
 *   className?       : string
 *   testId?          : string  (default 'harborwell-form-header')
 */
export function HarborWellFormHeader({
  eyebrow,
  title,
  supportingText,
  variant = "light",
  size = "md",
  align = "left",
  homeHref = "/",
  linkLogoHome = true,
  rightSlot = null,
  className = "",
  testId = "harborwell-form-header",
}) {
  const isDark = variant === "dark";

  // Logo height by size — preserves aspect ratio via w-auto.
  const logoClass = {
    sm: "h-8 w-auto",
    md: "h-10 w-auto",
    lg: "h-14 w-auto",
  }[size] || "h-10 w-auto";

  const alignClass = align === "center" ? "items-center text-center" : "items-start text-left";

  const eyebrowColor = isDark ? "text-[#F59E0B]" : "text-[#F59E0B]";
  const titleColor = isDark ? "text-white" : "text-[#0F172A]";
  const bodyColor = isDark ? "text-slate-200" : "text-[#334155]";

  const LogoImg = (
    <img
      src={BRAND.logo}
      alt={BRAND.logoAlt}
      className={logoClass}
      data-testid={`${testId}-logo`}
      loading="eager"
      decoding="async"
    />
  );

  return (
    <header
      className={`w-full flex ${align === "center" ? "flex-col items-center gap-3" : "items-center justify-between gap-4"} ${className}`}
      data-testid={testId}
      role="banner"
    >
      <div className={`flex ${align === "center" ? "flex-col items-center gap-2" : "items-center gap-3"}`}>
        {linkLogoHome ? (
          <Link
            to={homeHref}
            className="inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-[#7E937D]/50 rounded"
            data-testid={`${testId}-home-link`}
            aria-label={`${BRAND.orgName} home`}
          >
            {LogoImg}
          </Link>
        ) : (
          LogoImg
        )}

        {(eyebrow || title || supportingText) && (
          <div className={`flex flex-col ${alignClass} leading-tight`}>
            {eyebrow && (
              <div
                className={`text-xs uppercase tracking-widest font-semibold ${eyebrowColor}`}
                data-testid={`${testId}-eyebrow`}
              >
                {eyebrow}
              </div>
            )}
            {title && (
              <div
                className={`font-display text-lg sm:text-xl font-semibold ${titleColor}`}
                data-testid={`${testId}-title`}
              >
                {title}
              </div>
            )}
            {supportingText && (
              <div
                className={`text-sm ${bodyColor} mt-0.5 max-w-xl`}
                data-testid={`${testId}-supporting-text`}
              >
                {supportingText}
              </div>
            )}
          </div>
        )}
      </div>

      {rightSlot && (
        <div className="flex items-center gap-3" data-testid={`${testId}-right-slot`}>
          {rightSlot}
        </div>
      )}
    </header>
  );
}

export default HarborWellFormHeader;
